<div class="print-actions">
	<button (click)="doPrint()"><i class="fal fa-print"></i> Print</button>
</div>

<div class="sheet" *ngIf="loaded">

	<!-- Masthead: whose sheet, what it is, which program, machine and operator. -->
	<div class="mast">
		<div class="mast-logo"><img src="/assets/LogoFlat_White.png" alt="BPL Engineering Group" /></div>
		<!-- Every block reads the same three rows - label, value, detail - so they line up across the bar. -->
		<div class="mast-cell mast-title mast-wrap">
			<span>Toolroom</span>
			<strong>Tooling Work Sheet<ng-container *ngIf="operationLabel"> - <span class="nowrap">{{ operationLabel }}</span></ng-container></strong>
		</div>
		<div class="mast-cell mast-program">
			<span>Program ID</span>
			<strong class="num">{{ programNumber || '—' }}</strong>
			<em>Printed {{ printedAt | date: 'dd/MM/yyyy' }}</em>
		</div>
		<div class="mast-cell">
			<span>Machine Centre</span>
			<strong class="num">{{ itemRelations.machine?.cellNumber || '—' }}</strong>
			<em>{{ itemRelations.machine?.name }}</em>
		</div>
		<div class="mast-cell mast-wrap">
			<span>Operator</span>
			<strong>{{ operatorNames || '—' }}</strong>
		</div>
	</div>

	<!-- The job: reference image, the facts, and where to set it -->
	<section class="hero">
		<figure class="hero-image">
			<img *ngIf="item.imageUrl" [src]="item.imageUrl" alt="Program reference" />
			<div *ngIf="!item.imageUrl" class="no-image">No reference image</div>
		</figure>

		<div class="spec">
			<div class="spec-row">
				<div class="spec-cell"><span>Customer</span><strong>{{ itemRelations.part?.client?.name || '—' }}</strong></div>
				<div class="spec-cell"><span>Part Number</span><strong class="mono">{{ itemRelations.part?.partNumber || '—' }}</strong></div>
			</div>
			<div class="spec-row">
				<div class="spec-cell"><span>Tool</span><strong class="mono">{{ itemRelations.productionTool?.name || '—' }}</strong></div>
				<div class="spec-cell"><span>Billet</span><strong class="mono">{{ billetNames || '—' }}</strong></div>
			</div>
			<div class="spec-row">
				<div class="spec-cell"><span>Material</span><strong>{{ materialName || '—' }}</strong></div>
				<div class="spec-cell"><span>Billet Size</span><strong class="mono">{{ item.blockSize || '—' }}</strong></div>
			</div>
			<div class="spec-row spec-row-key">
				<div class="spec-cell key"><span>Datum</span><strong>{{ item.datum || '—' }}</strong></div>
				<div class="spec-cell key">
					<span>Set Up</span>
					<strong>
						<b class="flag" [class.yes]="item.viceSetUp">{{ item.viceSetUp ? 'Vice' : 'Clamp to Bed' }}</b>
						<small *ngIf="item.viceSetUp && item.viceStickout">Stickout <b class="mono">{{ item.viceStickout }}</b></small>
					</strong>
				</div>
			</div>
		</div>

		<aside class="side">
			<div class="dro">
				<div class="dro-label">Machine Coordinates</div>
				<div class="dro-axis"><b>X</b><span class="mono">{{ item.positionX ?? '' }}</span></div>
				<div class="dro-axis"><b>Y</b><span class="mono">{{ item.positionY ?? '' }}</span></div>
				<div class="dro-axis"><b>Z</b><span class="mono">{{ item.positionZ ?? '' }}</span></div>
			</div>
			<div class="thumbs">
				<figure>
					<div class="thumb"><img *ngIf="partImageUrl" [src]="partImageUrl" alt="Part" /></div>
					<figcaption>Part</figcaption>
				</figure>
				<figure>
					<div class="thumb"><img *ngIf="toolImageUrl" [src]="toolImageUrl" alt="Tool" /></div>
					<figcaption>Tool</figcaption>
				</figure>
			</div>
		</aside>
	</section>

	<!-- Setter instructions: the thing not to miss -->
	<section class="notes" *ngIf="item.notes">
		<div class="notes-label">Setter<br />Instructions</div>
		<p>{{ item.notes }}</p>
	</section>

	<!-- Cutting tools, in the order they're used -->
	<section class="tools">
		<div class="tools-head">
			<h2>Cutting Tools <small>in order of use</small></h2>
			<div class="stats">
				<div><b>{{ lines.length }}</b><span>Tools</span></div>
				<div><b>{{ roughingCount }}</b><span>Rough</span></div>
				<div><b>{{ finishingCount }}</b><span>Finish</span></div>
				<div class="run"><b>{{ totalRunTime || '—' }}</b><span>Total Run</span></div>
			</div>
		</div>
		<table>
			<thead>
				<tr>
					<th class="c-seq">#</th>
					<th class="c-tool">Tool</th>
					<th>Ø</th>
					<th>Type</th>
					<th class="c-left">Holder</th>
					<th class="c-stick">Stick Out</th>
					<th>Speed</th>
					<th>Feed</th>
					<th>R / F</th>
					<th>Run</th>
					<th class="c-sign">Op. Sign</th>
				</tr>
			</thead>
			<tbody>
				<tr *ngFor="let line of orderedLines">
					<td class="c-seq"><span class="seq">{{ (line.sequenceOrder || 0) + 1 }}</span></td>
					<td class="c-tool mono">{{ cuttingToolFor(line)?.toolNumber }}</td>
					<td class="mono">{{ line.diameter || cuttingToolFor(line)?.diameter }}</td>
					<td>{{ lineToolTypeName(line) }}</td>
					<td class="c-left">{{ toolHolderFor(line)?.name }}</td>
					<td class="c-stick mono">{{ line.stickOut }}</td>
					<td class="mono">{{ line.speed }}</td>
					<td class="mono">{{ line.feed }}</td>
					<td><span *ngIf="rfName(line.roughFinish)" class="rf" [class.rough]="rfName(line.roughFinish) === 'R'">{{ rfName(line.roughFinish) }}</span></td>
					<td class="mono">{{ formatRunTime(line.runTime) }}</td>
					<td class="c-sign"><small *ngIf="line.signedAt">{{ line.signedAt | date: 'dd/MM HH:mm' }}</small></td>
				</tr>
				<tr *ngIf="lines.length === 0">
					<td colspan="11" class="empty">No cutting tools on this program</td>
				</tr>
			</tbody>
		</table>
	</section>

	<!-- Who programmed it -->
	<div class="signoff" *ngIf="programmerName">
		<span>Programmed By</span>
		<strong>{{ programmerName }}</strong>
		<em *ngIf="itemRelations.programmerSignedAt">{{ itemRelations.programmerSignedAt | date: 'dd/MM/yyyy' }}</em>
	</div>
</div>
